import ButtonCst from "@components/ButtonCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { ColumnInterface } from "@components/Table/types";
import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import Step1 from "@pages/Contents/Corporate/CollectCampaigns/components/steps/Step1";
import Step2 from "@pages/Contents/Corporate/CollectCampaigns/components/steps/Step2";
import Step3 from "@pages/Contents/Corporate/CollectCampaigns/components/steps/Step3";
import Step4 from "@pages/Contents/Corporate/CollectCampaigns/components/steps/Step4";
import Step5 from "@pages/Contents/Corporate/CollectCampaigns/components/steps/Step5";
import {
  CollectCampaignDto,
  CollectCampaignGlobalStateInterface,
  CreateCollectCampaignDto,
  SetUsersDto,
  ValidationErrors,
} from "@pages/Contents/Corporate/CollectCampaigns/types";
import { submitCreation } from "@pages/Contents/Corporate/CollectCampaigns/utils/utils";
import {
  validateStep1,
  validateStep3,
  validateStep4,
  validateStep5,
} from "@pages/Contents/Corporate/CollectCampaigns/utils/validation";
import { onErrorMessage, ServicesURL } from "@utils/utilsApi";
import * as React from "react";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { fetcher, axiosInstance } from "../../../../../../services/config";
import { MethodType } from "../../../../../../services/type";
import { useCollectCampaignState } from "../../context/CollectCampaignContext";

interface NavigationProps {
  steps: string[];
  columns: ColumnInterface[] | undefined;
  onComplete: () => void | Promise<void>;
}

const Navigation: React.FC<NavigationProps> = ({
  steps,
  columns,
  onComplete,
}) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [output, setOutput] = useState<any>();
  const { state, dispatch } = useCollectCampaignState();

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    child?: ReactNode;
    message: string;
    description?: string;
  }>();

  const { id, idCampaign } = useParams();
  const theme = useTheme();
  const { t } = useTranslation("collect-campaign-page");

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();

  //VALIDATION
  const validateStep = (step: number): boolean => {
    let stepErrors: ValidationErrors = {};
    switch (step) {
      case 0:
        stepErrors = validateStep1(state);
        break;
      case 2:
        stepErrors = validateStep3(state);
        break;
      case 3:
        stepErrors = validateStep4(state);
        break;
      case 4:
        stepErrors = validateStep5(state, idCampaign);
        break;
      default:
        break;
    }

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const { trigger: triggerUpdateCampaign } = useSWRMutation<
    CollectCampaignGlobalStateInterface,
    Error,
    string,
    CollectCampaignGlobalStateInterface
  >(
    ServicesURL.updateCampaign
      .replace(":corporateId", id ? id : "1")
      .replace(":campaignId", idCampaign ? idCampaign : ""),
    async (url: string, { arg }) => {
      const updateDto: CollectCampaignDto = {
        id: idCampaign ? Number(idCampaign) : 0,
        corporate_id: id ? Number(id) : 1,
        name: arg.general?.name!,
        target: arg.general?.target!,
        unit: arg.general?.unit!,
        end_date: arg.general?.end_date,
        is_learner_visible: arg.general?.is_learner_visible,
        start_date: arg.general?.start_date,
      };
      await fetcher<CollectCampaignDto>(url, MethodType.PUT, updateDto);
      await fetcher<number[]>(
        ServicesURL.setCampaignLos
          .replace(":corporateId", id ? id : "1")
          .replace(":campaignId", `${idCampaign ?? ""}`),
        MethodType.PUT,
        arg.learning_objects?.map(({ id }) => id) ?? [],
      );
      if (arg.owners) {
        await fetcher<number[]>(
          ServicesURL.setCampaignOwners
            .replace(":corporateId", id ? id : "1")
            .replace(":campaignId", `${idCampaign ?? ""}`),
          MethodType.PUT,
          arg.owners,
        );
      }
      await fetcher<SetUsersDto>(
        ServicesURL.setCampaignUsers
          .replace(":corporateId", id ? id : "1")
          .replace(":campaignId", `${idCampaign ?? ""}`),
        MethodType.PUT,
        {
          groups: arg.users?.groups ?? [],
          exclusions: await arg.users?.exclusions?.text(),
        },
      );
      const formData = new FormData();
      formData.append(
        "certificate",
        new Blob([JSON.stringify(arg.template?.data ?? {})], {
          type: "application/json",
        }),
      );
      if (arg.template?.zip) {
        formData.append("template_zip", arg.template?.zip);
      }
      await axiosInstance.postForm(
        ServicesURL.createCampaignCertificateTemplate
          .replace(":corporateId", id ? id : "1")
          .replace(":campaignId", `${idCampaign ?? ""}`),
        formData,
      );
      return arg;
    },
  );

  const { trigger: triggerCreateCampaign } = useSWRMutation<
    CollectCampaignGlobalStateInterface,
    Error,
    string,
    CollectCampaignGlobalStateInterface
  >(
    ServicesURL.createCampaign.replace(":corporateId", id ? id : "1"),
    async (url, { arg }) => {
      const createDto: CreateCollectCampaignDto = {
        corporate_id: id ? Number(id) : 1,
        name: arg.general?.name!,
        target: arg.general?.target!,
        unit: arg.general?.unit!,
        end_date: arg.general?.end_date,
        is_learner_visible: !!arg.general?.is_learner_visible,
        start_date: arg.general?.start_date,
      };
      const resCreateCampaign: CollectCampaignDto =
        await fetcher<CreateCollectCampaignDto>(
          url,
          MethodType.POST,
          createDto,
        );
      const idCampaign = resCreateCampaign.id;
      await fetcher<number[]>(
        ServicesURL.setCampaignLos
          .replace(":corporateId", id ? id : "1")
          .replace(":campaignId", `${idCampaign ?? ""}`),
        MethodType.PUT,
        arg.learning_objects?.map(({ id }) => id) ?? [],
      );
      if (arg.owners) {
        await fetcher<number[]>(
          ServicesURL.setCampaignOwners
            .replace(":corporateId", id ? id : "1")
            .replace(":campaignId", `${idCampaign ?? ""}`),
          MethodType.PUT,
          arg.owners,
        );
      }
      await fetcher<SetUsersDto>(
        ServicesURL.setCampaignUsers
          .replace(":corporateId", id ? id : "1")
          .replace(":campaignId", `${idCampaign ?? ""}`),
        MethodType.PUT,
        {
          groups: arg.users?.groups ?? [],
          exclusions: await arg.users?.exclusions?.text(),
        },
      );
      const formData = new FormData();
      formData.append(
        "certificate",
        new Blob([JSON.stringify(arg.template?.data ?? {})], {
          type: "application/json",
        }),
      );
      formData.append("template_zip", arg.template?.zip!);
      await axiosInstance.postForm(
        ServicesURL.createCampaignCertificateTemplate
          .replace(":corporateId", id ? id : "1")
          .replace(":campaignId", `${idCampaign ?? ""}`),
        formData,
      );
      return arg;
    },
  );

  const handleNext = () => {
    if (!validateStep(activeStep)) return;
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);

    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleComplete = async () => {
    if (!validateStep(activeStep)) return;
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    try {
      let result;
      if (idCampaign) {
        result = await submitCreation(state, triggerUpdateCampaign);
        setOutput(result);
        onComplete();
      } else {
        result = await submitCreation(state, triggerCreateCampaign);
        setOutput(result);
        onComplete();
      }
      let warning;

      // if (result && !!result.response.users.usersExcludedErrors) {
      //   const usersNotFound = result.response.users.usersExcludedErrors;
      //   warning = (
      //     <>
      //       <Typography variant="h6" component={"p"}>
      //         {t("warning")}
      //       </Typography>
      //       <Typography variant="h6" component={"p"}>
      //         {t("some_users_not_found")}
      //       </Typography>
      //       <Typography variant="h6" component={"p"}>
      //         {usersNotFound.length} {t("users_not_found")}
      //       </Typography>
      //       <Typography variant="h6" component={"p"}>
      //         {usersNotFound.map((user: string) => (
      //           <List sx={{ p: 0, border: 0 }}>
      //             <ListItem>{user}</ListItem>
      //           </List>
      //         ))}
      //       </Typography>
      //     </>
      //   );
      // }
      setOpenToast({ s: StatusEnum.OK, child: warning, message: "" });
    } catch (error) {
      const message = onErrorMessage(error);
      setOpenToast({ s: StatusEnum.KO, message: message });
      console.error(error);
    }
  };

  const resetStep1 = () => {
    dispatch({
      type: "RESET_INPUT_VALUES",
      payload: ["general"],
    });
  };

  const resetStep2 = () => {
    dispatch({
      type: "RESET_INPUT_VALUES",
      payload: ["owners"],
    });
  };

  const resetStep3 = () => {
    dispatch({
      type: "RESET_INPUT_VALUES",
      payload: ["learning_objects"],
    });
  };

  const resetStep4 = () => {
    dispatch({
      type: "RESET_INPUT_VALUES",
      payload: ["users"],
    });
  };

  const resetStep5 = () => {
    dispatch({
      type: "RESET_INPUT_VALUES",
      payload: ["template"],
    });
  };

  const handleReset = () => {
    switch (activeStep) {
      case 0: {
        // Step1
        resetStep1();
        break;
      }
      case 1: {
        // Step2
        resetStep2();
        break;
      }
      case 2: {
        resetStep3();
        break;
      }
      case 3: {
        resetStep4();
        break;
      }
      case 4: {
        resetStep5();
        break;
      }
      default:
        break;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <Step1 columns={columns || []} errors={errors} />;
      case 1:
        return <Step2 />;
      case 2:
        return <Step3 errors={errors} />;
      case 3:
        return <Step4 errors={errors} />;
      case 4:
        return <Step5 errors={errors} />;
      default:
        return <Step1 columns={columns || []} errors={errors} />;
    }
  };

  return (
    <Box sx={{ width: "100%", padding: isSmallScreen ? 2 : 4 }}>
      <Box
        sx={{
          padding: "2.875rem 1.375rem",
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.common.white}`,
          borderRadius: "10px",
        }}
      >
        <Stepper
          nonLinear
          activeStep={activeStep}
          orientation={isSmallScreen ? "vertical" : "horizontal"}
          sx={{
            "&.MuiStepper-root": {
              paddingBottom: "2.875rem",
            },
          }}
        >
          {steps.map((label, index) => (
            <Step key={label} completed={completed[index]}>
              <StepButton color="inherit">
                <Box
                  style={{
                    color:
                      activeStep === index
                        ? theme.palette.accent.main
                        : theme.palette.text.primary,
                    textDecoration:
                      activeStep === index ? "underline" : "inherit",
                  }}
                >
                  {label}
                </Box>
              </StepButton>
            </Step>
          ))}
        </Stepper>
        <Typography sx={{ mt: 2, mb: 1, py: 1 }}>
          {renderStepContent()}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          pt: 4,
          justifyContent: "flex-end",
          [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
          },
        }}
      >
        <ButtonCst
          id={"resetButton"}
          variant={"text"}
          size={"medium"}
          sx={{
            minWidth: "150px",
            [theme.breakpoints.down("lg")]: {
              minWidth: "100px",
            },
            [theme.breakpoints.down("sm")]: {
              minWidth: "100%",
            },
          }}
          onClick={handleReset}
        >
          {t("resetButton")}
        </ButtonCst>
        {activeStep !== 0 && (
          <ButtonCst
            id={"back"}
            variant={"outlined"}
            size={"medium"}
            sx={{
              minWidth: "150px",
              [theme.breakpoints.down("lg")]: {
                minWidth: "100px",
              },
              [theme.breakpoints.down("sm")]: {
                minWidth: "100%",
              },
            }}
            onClick={handleBack}
          >
            {t("back")}
          </ButtonCst>
        )}
        <ButtonCst
          id={"confirm"}
          variant={"contained"}
          size={"medium"}
          sx={{
            minWidth: "150px",
            [theme.breakpoints.down("lg")]: {
              minWidth: "100px",
            },
            [theme.breakpoints.down("sm")]: {
              minWidth: "100%",
            },
          }}
          onClick={() =>
            activeStep === steps.length - 1 ? handleComplete() : handleNext()
          }
        >
          {activeStep === steps.length - 1 ? `${t("finish")}` : `${t("next")}`}
        </ButtonCst>
      </Box>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </Box>
  );
};

export default Navigation;
